import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";

import { LoadingModal } from "../../../components/LoadingModal";
import { Crumb, ScreenHeader } from "../../../components/SubHeader";
import constants from "../../../constants";
import { api, ApiSiteResponse, hasActiveSubscription, User } from "../../../services/api";
import { ChatArea, Container, Section } from "../styles";

/** Whether the user has at least one PLUS Keepo (chat support is a PLUS feature). Undefined while loading. */
export const useHasPlusKeepo = () => {
  const [hasPlus, setHasPlus] = useState<boolean>();

  useEffect(() => {
    api
      .getSites()
      .then(sites => setHasPlus(sites.some(({ subscription }) => hasActiveSubscription(subscription))))
      .catch(() => setHasPlus(false));
  }, []);

  return hasPlus;
};

const CHATWOOT_EVENT_PREFIX = "chatwoot-widget:";

// The widget's session (its visitor and conversations), kept per account like the SDK's `cw_conversation` cookie
const sessionKey = (user: User) => `Keepo.chatwoot.${user.email}`;

const readSession = (user: User) => {
  try {
    return localStorage.getItem(sessionKey(user)) || undefined;
  } catch {
    return undefined;
  }
};

const saveSession = (user: User, token: string) => {
  try {
    localStorage.setItem(sessionKey(user), token);
  } catch {
    // No storage (private mode): the chat still works, just without history on the next visit
  }
};

/** What the support agent sees about the visitor, as Chatwoot custom attributes. */
const visitorAttributes = (user: User, sites: ApiSiteResponse[]) => {
  const keepos = sites.filter(({ site }) => !!site);
  return {
    keepo_name: user.name,
    keepo_email: user.email,
    keepo_plan: keepos.some(({ subscription }) => hasActiveSubscription(subscription)) ? "PLUS" : "FREE",
    keepo_keepos: keepos
      .map(
        ({ site, subscription }) =>
          `${site.title} (${site.url}.${constants.nameSite}, ${hasActiveSubscription(subscription) ? "PLUS" : "FREE"})`
      )
      .join("; "),
  };
};

/**
 * Talks to the embedded widget the way Chatwoot's own SDK does: once it says it has loaded, send the
 * config (dark mode, our language; it's also what makes it fetch the visitor's past conversations),
 * mark it as open and tell who the visitor is. Its session token is saved so the next visit
 * continues the same conversations (`set-user` also sends a new one, for the identified contact).
 */
const useChatwootWidget = (
  iframe: React.RefObject<HTMLIFrameElement>,
  language: string,
  user: User,
  sites: ApiSiteResponse[]
) => {
  useEffect(() => {
    const { baseUrl } = constants.chatwoot;
    const send = (message: object) =>
      iframe.current?.contentWindow?.postMessage(`${CHATWOOT_EVENT_PREFIX}${JSON.stringify(message)}`, baseUrl);

    const onMessage = (event: MessageEvent) => {
      if (
        event.origin !== baseUrl ||
        event.source !== iframe.current?.contentWindow ||
        typeof event.data !== "string" ||
        !event.data.startsWith(CHATWOOT_EVENT_PREFIX)
      ) {
        return;
      }
      const message = JSON.parse(event.data.slice(CHATWOOT_EVENT_PREFIX.length));

      if (message.event === "setAuthCookie" && message.data?.widgetAuthToken) {
        saveSession(user, message.data.widgetAuthToken);
        return;
      }
      if (message.event !== "loaded") {
        return;
      }
      // Tokens the widget created before this one was saved (its first load for this account)
      if (message.config?.authToken) {
        saveSession(user, message.config.authToken);
      }
      send({
        event: "config-set",
        locale: language.toLowerCase().startsWith("pt") ? "pt_BR" : "en",
        darkMode: "dark",
        position: "right",
        hideMessageBubble: true,
        showPopoutButton: false,
        showUnreadMessagesDialog: false,
      });
      send({ event: "set-color-scheme", darkMode: "dark" });
      send({ event: "toggle-open", isOpen: true });
      // Who the visitor is: the contact gets the account's name and e-mail (with the plan and Keepos as
      // attributes) and the same conversations on any browser. The identifier is the internal id, not
      // the e-mail: without Chatwoot's identity validation (HMAC) whoever sends an identifier gets that
      // contact's history, so it must be something others can't guess.
      if (user.id) {
        send({
          event: "set-user",
          identifier: user.id,
          user: { name: user.name, email: user.email, custom_attributes: visitorAttributes(user, sites) },
        });
      } else {
        send({ event: "set-custom-attributes", customAttributes: visitorAttributes(user, sites) });
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [iframe, language, user, sites]);
};

type ChatProps = {
  user: User;
  sites: ApiSiteResponse[];
};

const Chat = ({ user, sites }: ChatProps) => {
  const { t, i18n } = useTranslation();
  const iframe = useRef<HTMLIFrameElement>(null);
  useChatwootWidget(iframe, i18n.language, user, sites);

  // Read once: the saved session only matters for the first load of the iframe
  const [session] = useState(() => readSession(user));
  const { baseUrl, websiteToken } = constants.chatwoot;
  const src = `${baseUrl}/widget?website_token=${websiteToken}${
    session ? `&cw_conversation=${encodeURIComponent(session)}` : ""
  }#/messages`;

  return (
    // Chatwoot's widget screen, embedded instead of the floating bubble; `#/messages` opens
    // straight on the conversation instead of the widget's home
    <ChatArea>
      <iframe ref={iframe} title={t("pages.help.chat.header.title")} src={src} allow="clipboard-write; microphone" />
    </ChatArea>
  );
};

/** Chat support with the Keepo team, PLUS only: opening it without a PLUS Keepo goes back to /help. */
export const HelpChatPage = () => {
  const { t } = useTranslation();
  // Screens that link straight here (e.g. the PLUS benefits) pass where to go back to; otherwise, Help
  const back = (useLocation().state as { back?: Crumb } | null)?.back;
  const [data, setData] = useState<{ user: User; sites: ApiSiteResponse[] } | null>();

  useEffect(() => {
    Promise.all([api.getUser(), api.getSites()])
      .then(([user, sites]) => setData({ user, sites }))
      .catch(() => setData(null));
  }, []);

  if (data === undefined) {
    return <LoadingModal />;
  }
  if (!data || !data.sites.some(({ subscription }) => hasActiveSubscription(subscription))) {
    return <Navigate to="/help" replace />;
  }

  return (
    <Container>
      <ScreenHeader
        title={t("pages.help.chat.header.title")}
        back={back || { label: t("pages.help.header.title"), to: "/help" }}
      />
      <Section>
        <Chat user={data.user} sites={data.sites} />
      </Section>
    </Container>
  );
};
